import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema, slugSchema } from './comum.js';
/**
 * ============================================================================
 * CLIENTE — a tabela quente
 * ============================================================================
 *
 * Secao 12 do diagnostico: tabela quente e tabela estreita. Tudo que e grande e
 * lido raramente sai da tabela que o painel consulta o tempo todo.
 *
 * O que saiu de `clients`:
 *   html_content  -> R2 (site_versions.chaveR2)
 *   data (JSONB)  -> dividido em duas partes:
 *                      as colunas de negocio consultadas viraram colunas de verdade
 *                      (nome, telefone, endereco, nota, avaliacoes, categorias);
 *                      o resto (blueprint, avaliacoes cruas, analise de fotos,
 *                      horarios) foi para `dados_negocio`, tabela fria 1:1.
 *   data->>pin    -> `credenciais_cliente`, com hash argon2id. Nunca mais na mesma
 *                    linha que dado de negocio, nunca mais em texto puro.
 */
export const statusClienteSchema = z.enum([
    'lead',
    'pendente',
    'ativo',
    'inadimplente',
    'cancelado',
]);
/**
 * O gasto de IA JA ACUMULADO de um cliente, somado no ato de cada geracao.
 *
 * Nao e consulta: sao colunas de `clientes` incrementadas na MESMA transacao que
 * grava a linha de `uso_ia`. A soma por agregacao continua existindo (`ia.uso`,
 * `ia.custoPorSite`) e continua sendo a fonte da verdade — o acumulador existe
 * para a LISTAGEM poder mostrar "R$ 0,42 de IA" ao lado de cada cliente sem um
 * `group by` sobre a tabela de uso a cada carregamento de tela.
 *
 * `usd`/`brl` sao o que foi possivel calcular. `chamadasSemPreco` conta as que
 * usaram modelo sem preco cadastrado: o dinheiro saiu e nao entra nessa soma —
 * por isso o numero aparece separado, em vez de somar zero e sumir.
 */
export const custoIaAcumuladoSchema = z.object({
    chamadas: z.number().int(),
    chamadasSemPreco: z.number().int(),
    tokens: z.number().int(),
    usd: z.number(),
    brl: z.number(),
    /** Quando a ultima chamada foi somada aqui. `null` = nunca gastou IA. */
    atualizadoEm: dataHoraSchema.nullable(),
});
/** O que o painel lista. Tudo que esta aqui cabe num indice; nada aqui e grande. */
export const clienteSchema = z.object({
    id: z.uuid(),
    slug: slugSchema,
    nome: z.string(),
    telefone: z.string().nullable(),
    email: z.email().nullable(),
    endereco: z.string().nullable(),
    categoria: z.string().nullable(),
    nota: z.number().nullable(),
    totalAvaliacoes: z.number().int().nullable(),
    status: statusClienteSchema,
    vendedorId: z.string().nullable(),
    precoPersonalizado: z.number().nullable(),
    /** Denormalizado do site principal, para a listagem nao precisar de join. */
    siteId: z.uuid().nullable(),
    dominioPrincipal: z.string().nullable(),
    /** Gasto de IA acumulado, somado a cada geracao. Nunca estimado. */
    custoIa: custoIaAcumuladoSchema,
    criadoEm: dataHoraSchema,
    atualizadoEm: dataHoraSchema,
});
/**
 * Tabela fria 1:1 com cliente. So e lida quando alguem abre o dossie.
 * Fica separada exatamente para nao ser arrastada por uma listagem.
 */
export const dadosNegocioSchema = z.object({
    clienteId: z.uuid(),
    placeId: z.string().nullable(),
    placeUri: z.string().nullable(),
    website: z.string().nullable(),
    horarios: z.unknown().nullable(),
    avaliacoesTopo: z.array(z.unknown()).default([]),
    categorias: z.array(z.string()).default([]),
    comodidades: z.array(z.string()).default([]),
    palavrasChaveSeo: z.array(z.string()).default([]),
    resumo: z.string().nullable(),
    analiseFotos: z.string().nullable(),
    blueprint: z
        .array(z.object({
        title: z.string(),
        uxStrategy: z.string(),
        description: z.string(),
        visualSuggestion: z.string(),
    }))
        .default([]),
    faixaPreco: z.string().nullable(),
    statusNegocio: z.string().nullable(),
    campanhaId: z.string().nullable(),
    alvoId: z.string().nullable(),
    atualizadoEm: dataHoraSchema,
});
export const criarClienteSchema = z.object({
    nome: z.string().min(1),
    slug: slugSchema.optional(),
    telefone: z.string().optional(),
    email: z.email().optional(),
    endereco: z.string().optional(),
    categoria: z.string().optional(),
    nota: z.number().optional(),
    totalAvaliacoes: z.number().int().optional(),
    status: statusClienteSchema.default('lead'),
    vendedorId: z.string().nullable().optional(),
    precoPersonalizado: z.number().nullable().optional(),
    dadosNegocio: dadosNegocioSchema
        .omit({ clienteId: true, atualizadoEm: true })
        .partial()
        .optional(),
    /** Credencial do portal do cliente. O PIN vira hash argon2id no servidor. */
    credencial: z
        .object({
        usuario: z.string().min(3).max(80),
        /** Opcional: ausente faz o servidor gerar com CSPRNG, nunca com Math.random(). */
        pin: z.string().min(4).max(12).optional(),
    })
        .optional(),
});
export const atualizarClienteSchema = criarClienteSchema.partial().omit({ credencial: true });
/**
 * Importacao em lote vinda da fabrica (`criador-sites`).
 *
 * Devolve `salvos` e `ignorados` porque o comportamento antigo — inserir um a um
 * para contornar o 23505 de slug repetido — precisa continuar existindo, so que
 * agora resolvido no banco com ON CONFLICT DO NOTHING em uma unica ida.
 */
export const importarClientesSchema = z.object({
    clientes: z.array(criarClienteSchema).min(1).max(500),
});
export const resultadoImportacaoSchema = z.object({
    salvos: z.number().int(),
    ignorados: z.number().int(),
    idsCriados: z.array(z.uuid()),
    /**
     * O par slug -> id de quem NASCEU agora.
     *
     * `idsCriados` sozinho e ambiguo e ja produziu erro: quem chamava fazia
     * `idsCriados.entries()` e indexava a lista ENVIADA pela mesma posicao — o que
     * so bate quando nenhum slug ja existia. Um repetido no meio do lote desloca
     * tudo dali para a frente, e o efeito e o template (ou a foto) de um cliente
     * indo para o cadastro do vizinho, sem erro nenhum em tela.
     *
     * Com o slug ao lado do id, quem chamou casa pelo que enviou. `idsCriados`
     * continua existindo para quem so precisa da contagem.
     */
    criados: z.array(z.object({ slug: z.string(), id: z.uuid() })).default([]),
});
export const filtroClientesSchema = paginacaoSchema.extend({
    busca: z.string().optional(),
    status: statusClienteSchema.optional(),
    vendedorId: z.string().optional(),
    /** Janela de criacao. `hoje` e o padrao herdado do painel. */
    periodo: z.enum(['hoje', '7d', '30d', 'todos', 'custom']).default('todos'),
    de: z.string().optional(),
    ate: z.string().optional(),
    /** Inclui `dadosNegocio` no resultado. Falso na listagem, por desenho. */
    comDadosNegocio: z.boolean().default(false),
});
/** Cerebro do negocio: 20 blocos de dossie. Fria, so lida na tela do dossie. */
export const cerebroNegocioSchema = z.object({
    clienteId: z.uuid(),
    dadosEmpresa: z.unknown().nullable(),
    responsaveis: z.unknown().nullable(),
    detalhesNegocio: z.unknown().nullable(),
    produtos: z.unknown().nullable(),
    clienteIdeal: z.unknown().nullable(),
    concorrentes: z.unknown().nullable(),
    processoVendas: z.unknown().nullable(),
    metaAds: z.unknown().nullable(),
    googleAds: z.unknown().nullable(),
    whatsapp: z.unknown().nullable(),
    crm: z.unknown().nullable(),
    automacoes: z.unknown().nullable(),
    conteudo: z.unknown().nullable(),
    provasSociais: z.unknown().nullable(),
    financeiro: z.unknown().nullable(),
    objetivos: z.unknown().nullable(),
    documentosGerais: z.unknown().nullable(),
    baseConhecimento: z.unknown().nullable(),
    marca: z.unknown().nullable(),
    biblioteca: z.unknown().nullable(),
    materiaisApoio: z.unknown().nullable(),
    conexoes: z.unknown().nullable(),
    atualizadoEm: dataHoraSchema,
});
export const salvarCerebroNegocioSchema = cerebroNegocioSchema
    .omit({ clienteId: true, atualizadoEm: true })
    .partial();
//# sourceMappingURL=cliente.js.map